import { bringColorCloserToBackground } from "@buzzeasy/shared-frontend-utilities";
import { ConfigProvider, GlobalToken, ThemeConfig, theme } from "antd";
import { PropsWithChildren, ReactElement, useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import appTheme, { AppThemeSet, graiaDefaultFont, graiaPrimaryColor } from "../../../appTheme";
import { antLocales } from "../../../translations";
import { colorToThemeTokens } from "../../../utils/antDesignUtils";
import { getSystemColorScheme } from "../../../utils/cssUtils";
import { maskZIndex } from "../../layouts/Mask";
import { SettingsContext } from "../SettingsContext";
import ThemeOverrideProvider from "../ThemeOverrideProvider";
import { AntDesignConfigContext, setColorScheme } from "./AntDesignThemeContext";
import { HostThemeConfigContext } from "./HostThemeConfigContext";

const componentThemeOverrides: ThemeConfig["components"] = {
  Message: {
    zIndexPopupBase: maskZIndex + 1,
  },
  Notification: {
    zIndexPopupBase: maskZIndex + 1,
  },
};

export default function AntDesignThemeProvider({ children }: PropsWithChildren): ReactElement {
  const [{ colorScheme }] = useContext(SettingsContext);
  const hostThemeConfig = useContext(HostThemeConfigContext);
  const { i18n } = useTranslation();

  const actualColorScheme = useMemo(
    () => colorScheme ?? getSystemColorScheme(),
    [colorScheme],
  );

  // update the outside reference on render and not useEffect in order to not skip the initial render
  setColorScheme(actualColorScheme);

  const hostThemeSet = useMemo<AppThemeSet | null>(
    () => {
      if (!hostThemeConfig)
        return null;

      return {
        light: {
          token: {
            ...hostThemeConfig.theme.light,
            fontFamily: hostThemeConfig.theme.light.fontFamily
              ?? graiaDefaultFont,
            ...colorToThemeTokens("primary", hostThemeConfig.theme.light.colorPrimary ?? graiaPrimaryColor),
            ...colorToThemeTokens("info", hostThemeConfig.theme.light.colorInfo ?? hostThemeConfig.theme.light.colorPrimary ?? graiaPrimaryColor),
          },
          components: componentThemeOverrides,
          algorithm: theme.defaultAlgorithm,
        },
        dark: {
          token: {
            ...hostThemeConfig.theme.dark,
            fontFamily: hostThemeConfig.theme.dark?.fontFamily
              ?? hostThemeConfig.theme.light.fontFamily
              ?? graiaDefaultFont,
            colorPrimary: hostThemeConfig.theme.dark?.colorPrimary
              ?? hostThemeConfig.theme.light.colorPrimary
              ?? graiaPrimaryColor,
            colorInfo: hostThemeConfig.theme.dark?.colorInfo
              ?? hostThemeConfig.theme.dark?.colorPrimary
              ?? hostThemeConfig.theme.light.colorInfo
              ?? hostThemeConfig.theme.light.colorPrimary
              ?? graiaPrimaryColor,
          },
          components: componentThemeOverrides,
          algorithm: theme.darkAlgorithm,
        },
      };
    },
    [hostThemeConfig],
  );

  const themeConfig = (hostThemeSet ?? appTheme)[actualColorScheme];

  const overrideTokens = useCallback(
    (token: GlobalToken) => ({
      colorPrimaryBg: colorScheme === "light" ? bringColorCloserToBackground(token.colorPrimaryBg, token.colorBgContainer, 1.125, 0.1) : token.colorPrimaryBg,
    }),
    [colorScheme],
  );

  return (
    <AntDesignConfigContext.Provider value={themeConfig}>
      <ConfigProvider theme={themeConfig} locale={antLocales[i18n.language]}>
        <ThemeOverrideProvider overrideTokens={overrideTokens}>
          {children}
        </ThemeOverrideProvider>
      </ConfigProvider>
    </AntDesignConfigContext.Provider>
  );
}
